<template>
    <TprmModal
        additional-class-name="EditEmailTemplateModal"
        size="lg"
        :allow-close="!submitting"
        :open="open"
        title="Edit email template"
        @close="$emit('close')"
    >
        <Tabs
            v-if="producerInitialValue"
            :selected-tab="activeAudience"
            @change="onTabChange"
        >
            <!-- One form per tab, so switching tabs never shows the other audience's text. -->
            <Tab :id="audiences.entity" name="Entity">
                <form @submit.prevent="onSubmit">
                    <JsonSchemaCustomForm v-slot="{ fields }" v-model="value" :disabled="submitting" :form="form">
                        <JsonSchemaCustomFormElement :field="fields.summaryTemplate" />
                        <JsonSchemaCustomFormElement :field="fields.descriptionTemplate" />
                    </JsonSchemaCustomForm>
                </form>
            </Tab>
            <Tab :id="audiences.producer" name="Producer">
                <form @submit.prevent="onSubmit">
                    <JsonSchemaCustomForm
                        v-slot="{ fields }"
                        v-model="producerValue"
                        :disabled="submitting"
                        :form="form"
                    >
                        <JsonSchemaCustomFormElement :field="fields.summaryTemplate" />
                        <JsonSchemaCustomFormElement :field="fields.descriptionTemplate" />
                    </JsonSchemaCustomForm>
                </form>
            </Tab>
        </Tabs>
        <form v-else @submit.prevent="onSubmit">
            <JsonSchemaCustomForm v-slot="{ fields }" v-model="value" :disabled="submitting" :form="form">
                <JsonSchemaCustomFormElement :field="fields.summaryTemplate" />
                <JsonSchemaCustomFormElement :field="fields.descriptionTemplate" />
            </JsonSchemaCustomForm>
        </form>

        <template #footer>
            <NewButton
                color="primary"
                :loading="submitting"
                :disabled="!valid || submitting"
                @click="onSubmit"
            >
                Save
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import {
        JsonSchemaCustomForm,
        JsonSchemaCustomFormElement,
    } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import { Tab, Tabs } from '@evidentid/dashboard-commons/components/Tabs';
    import createForm from '@evidentid/json-schema/createForm';
    import {
        NotificationAudience,
        NotificationConfiguration,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    import { buildEmailTemplateSchema } from '../schemas/buildEmailTemplateSchema';

    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'submit' but it is
    // neither declared in the emits option nor as an 'onClose'/'onSubmit' prop" every
    // time it fires. This is the family observed live during MODE-3 QA at
    // #/aleksandrpf/notifications/email-settings.
    @Component({
        components: {
            TprmModal,
            NewButton,
            JsonSchemaCustomForm,
            JsonSchemaCustomFormElement,
            Tab,
            Tabs,
        },
        emits: [ 'close', 'submit' ],
    })
    export default class EditEmailTemplateModal extends Vue {
        @Prop({ type: Boolean, default: false })
        private open!: boolean;

        @Prop({ type: Boolean, default: false })
        private submitting!: boolean;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: string[];

        @Prop({ type: Object, default: () => ({}) })
        private initialValue!: NotificationConfiguration;

        // The producer's text, given only with producer flow on and for a type producers receive.
        @Prop({ type: Object, default: null })
        private producerInitialValue!: NotificationConfiguration | null;

        private audiences = NotificationAudience;
        private activeAudience: NotificationAudience = NotificationAudience.entity;
        private form = createForm(buildEmailTemplateSchema(this.customProperties));
        private value = this.form.getValue(this.initialValue, true);
        private producerValue = this.form.getValue(this.producerInitialValue || {}, true);

        @Watch('customProperties')
        private onCustomPropertiesChange(): void {
            this.form = createForm(buildEmailTemplateSchema(this.customProperties));
        }

        private get editingProducer(): boolean {
            return this.activeAudience === NotificationAudience.producer && this.producerInitialValue != null;
        }

        // Each tab is saved only when edited, so the other tab's edits are not dropped while the save
        // reports success. The entity tab is also saved when it is the one on screen, as before tabs
        // existed. An unedited Producer tab is never saved: until the RP has producer text of its own,
        // that tab shows the entity text as a fallback, and saving it would freeze that copy.
        private get submissions(): { audience: NotificationAudience, template: NotificationConfiguration }[] {
            // The PUT replaces the notification type's entire configuration, so fields the form does not
            // edit (e.g. initEnabled, remindersEnabled) are carried through from the initial value.
            const entity = {
                audience: NotificationAudience.entity,
                template: { ...this.initialValue, ...this.value },
            };
            if (this.producerInitialValue == null) {
                return [ entity ];
            }
            const producer = {
                audience: NotificationAudience.producer,
                template: { ...this.producerInitialValue, ...this.producerValue },
            };
            const entityEdited = this.isEdited(entity.template, this.initialValue);
            const producerEdited = this.isEdited(producer.template, this.producerInitialValue);
            return [
                ...(entityEdited || !this.editingProducer ? [ entity ] : []),
                ...(producerEdited ? [ producer ] : []),
            ];
        }

        private isEdited(template: NotificationConfiguration, initial: NotificationConfiguration): boolean {
            return template.summaryTemplate !== initial.summaryTemplate
                || template.descriptionTemplate !== initial.descriptionTemplate;
        }

        private get valid(): boolean {
            return this.submissions.every(({ template }) => this.form.isValid(template, true));
        }

        private onTabChange(audience: NotificationAudience): void {
            this.activeAudience = audience;
        }

        private onSubmit(event: Event): void {
            event.preventDefault();
            if (this.valid && !this.submitting) {
                this.$emit('submit', this.submissions);
            }
        }
    }
</script>
